import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import ListingCard from '@/components/marketplace/ListingCard';
import { Heart, Users, ArrowRight, Building2, BadgeCheck } from 'lucide-react';

export const MyFavorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/favorites/mine')
      .then(r => setFavorites(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className='space-y-5' data-testid='my-favorites-page'>
      <div>
        <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>My Favorites</h1>
        <p className='text-muted-foreground text-sm'>Listings you saved for later. Only you can see this list.</p>
      </div>
      {loading ? (
        <div className='text-sm text-muted-foreground py-16 text-center'>Loading…</div>
      ) : favorites.length === 0 ? (
        <Card className='p-10 text-center'>
          <Heart className='h-8 w-8 mx-auto text-muted-foreground' />
          <div className='mt-3 font-semibold'>No favorites yet</div>
          <p className='text-sm text-muted-foreground'>Tap the heart icon on any listing to save it here.</p>
        </Card>
      ) : (
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
          {favorites.map(l => <ListingCard key={l.id} listing={l} />)}
        </div>
      )}
    </div>
  );
};

export const MyFollowing = () => {
  const [follows, setFollows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/follows/mine')
      .then(r => setFollows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className='space-y-5' data-testid='my-follows-page'>
      <div>
        <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>Following</h1>
        <p className='text-muted-foreground text-sm'>Sellers you follow. New listings from them will appear on your dashboard.</p>
      </div>
      {loading ? (
        <div className='text-sm text-muted-foreground py-16 text-center'>Loading…</div>
      ) : follows.length === 0 ? (
        <Card className='p-10 text-center'>
          <Users className='h-8 w-8 mx-auto text-muted-foreground' />
          <div className='mt-3 font-semibold'>Not following anyone yet</div>
          <p className='text-sm text-muted-foreground'>Open a listing detail and follow the seller from the sidebar.</p>
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3'>
          {follows.map(s => (
            <Card key={s.id} className='p-4 flex items-start gap-3' data-testid={`follow-row-${s.id}`}>
              <div className='h-11 w-11 rounded-lg bg-secondary flex items-center justify-center shrink-0'>
                <Building2 className='h-5 w-5 text-primary' />
              </div>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-1.5 flex-wrap'>
                  <Link to={`/seller/${s.id}`} className='font-semibold hover:underline underline-offset-4'>{s.company_name || s.name}</Link>
                  {s.is_gst_verified && (
                    <span className='inline-flex items-center gap-1 text-[10px] font-medium rounded-full bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))] px-1.5 py-0.5'>
                      <BadgeCheck className='h-3 w-3' /> GST
                    </span>
                  )}
                </div>
                {s.followed_at && (
                  <div className='text-[11px] text-muted-foreground mt-0.5'>
                    Followed on {new Date(s.followed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
              <Link to={`/seller/${s.id}`} className='text-primary text-xs hover:underline inline-flex items-center gap-0.5 mt-1'>
                View <ArrowRight className='h-3 w-3' />
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyFavorites;
